import { useFlags } from "launchdarkly-react-client-sdk";

import { Navigate, useLocation, useParams } from "src/router";
import type { RouteObject } from "src/router";
import { PageSpinner } from "src/components/loading";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useUser } from "src/contexts/user-context";
import { useContractOutletContextIfAvailable } from "src/events/contracts/outlet";
import { isPermissionsTabAccessible } from "src/events/permissions-tab-access";
import { lazyNamed } from "src/utils/lazy-named";

const DomainList = lazyNamed(
  () => import("./components/domain-list"),
  "DomainList",
);
const DomainLoader = lazyNamed(
  () => import("./domain/domain-loader"),
  "DomainLoader",
);
const DomainDetailLayout = lazyNamed(
  () => import("./domain/domain"),
  "DomainDetailLayout",
);
const DomainSchemas = lazyNamed(
  () => import("./domain/domain-schemas"),
  "DomainSchemas",
);
const DomainPermissions = lazyNamed(
  () => import("./domain/domain-permissions"),
  "DomainPermissions",
);
const CreateEventSchema = lazyNamed(
  () => import("./domain/create-event-schema"),
  "CreateEventSchema",
);
const CreateEventComponent = lazyNamed(
  () => import("./domain/create-event-component"),
  "CreateEventComponent",
);
const EventSchema = lazyNamed(
  () => import("./domain/event-schema"),
  "EventSchema",
);
const DomainSources = lazyNamed(
  () => import("./domain/domain-sources"),
  "DomainSources",
);
const DomainComponents = lazyNamed(
  () => import("./domain/domain-components"),
  "DomainComponents",
);
const EventComponentDetail = lazyNamed(
  () => import("./domain/event-component-detail"),
  "EventComponentDetail",
);
const EventComponentSchema = lazyNamed(
  () => import("./components/event-component-schema"),
  "EventComponentSchema",
);
const EventComponentUsage = lazyNamed(
  () => import("./components/event-component-usage"),
  "EventComponentUsage",
);
const ContractsLayout = lazyNamed(
  () => import("./components/contracts-layout"),
  "ContractsLayout",
);
const ComponentLibrary = lazyNamed(
  () => import("./components/component-library"),
  "ComponentLibrary",
);

// Gates the Permissions tab the same way the Contracts UI does: hidden unless
// `eventsRbacEnabled` and the user can update the domain (or created it). Static
// because it calls hooks.
const DomainPermissionsRoute = () => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const { eventsRbacEnabled } = useFlags();
  const domainContext = useContractOutletContextIfAvailable();
  const domain = domainContext?.contract;
  const permissions = useResourcePermission(
    domain == null
      ? undefined
      : {
          v2: {
            resource: "event_plan",
            grant: "can_update",
            id: domain.id,
          },
        },
  );
  const showPermissions = isPermissionsTabAccessible({
    eventsRbacEnabled,
    resource: domain,
    user,
    permissions,
  });

  if (!eventsRbacEnabled) {
    return (
      <Navigate
        to={{ pathname: "..", search: location.search }}
        relative="path"
        replace
      />
    );
  }

  if (!domain) {
    return id ? <PageSpinner /> : <Navigate to="/events/domains" replace />;
  }

  if (permissions.isLoading) {
    return <PageSpinner />;
  }

  if (!showPermissions) {
    return (
      <Navigate
        to={{ pathname: "..", search: location.search }}
        relative="path"
        replace
      />
    );
  }

  return <DomainPermissions />;
};

export const routes: Array<RouteObject> = [
  {
    element: <ContractsLayout />,
    spacesAccess: "hidden-from-limited-view",
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <DomainList />,
      },
      {
        path: "components",
        spacesAccess: "hidden-from-limited-view",
        element: <ComponentLibrary />,
      },
    ],
  },
  {
    path: ":id",
    spacesAccess: "hidden-from-limited-view",
    element: <DomainLoader />,
    children: [
      {
        element: <DomainDetailLayout />,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <DomainSchemas />,
          },
          {
            path: "components",
            spacesAccess: "hidden-from-limited-view",
            element: <DomainComponents />,
          },
          {
            path: "sources",
            spacesAccess: "hidden-from-limited-view",
            element: <DomainSources />,
          },
          {
            path: "permissions",
            spacesAccess: "hidden-from-limited-view",
            element: <DomainPermissionsRoute />,
          },
        ],
      },
      {
        path: "new_event",
        spacesAccess: "hidden-from-limited-view",
        element: <CreateEventSchema />,
      },
      {
        path: "new_component",
        spacesAccess: "hidden-from-limited-view",
        element: <CreateEventComponent />,
      },
      {
        path: "components/:component_id",
        element: <EventComponentDetail />,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <EventComponentSchema />,
          },
          {
            path: "usage",
            spacesAccess: "hidden-from-limited-view",
            element: <EventComponentUsage />,
          },
        ],
      },
      {
        path: ":event_type_and_name/:event_version",
        spacesAccess: "hidden-from-limited-view",
        element: <EventSchema />,
      },
    ],
  },
];
